'use client';

import React, { useState, useCallback } from 'react';
import { Bookmark, Check } from 'lucide-react';
import { usePersonalTools } from '@/hooks/usePersonalTools';

export interface BookmarkButtonProps {
  slug: string;
  name?: string;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export function BookmarkButton({
  slug,
  name,
  className = '',
  variant = 'outline',
  size = 'md',
  showLabel = true,
}: BookmarkButtonProps) {
  const { isBookmarked, toggleBookmark, isHydrated } = usePersonalTools();
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const bookmarked = isHydrated ? isBookmarked(slug) : false;

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 2400);
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleBookmark(slug);
    if (!bookmarked) {
      showToast(name ? `Đã lưu "${name}" vào danh sách yêu thích!` : 'Đã lưu tiện ích vào danh sách!');
    } else {
      showToast(name ? `Đã bỏ lưu "${name}"` : 'Đã bỏ lưu tiện ích!');
    }
  };

  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-xs gap-1.5 rounded-lg',
    md: 'px-3.5 py-2 text-sm gap-2 rounded-xl',
    lg: 'px-4 py-2.5 text-base gap-2.5 rounded-xl font-medium',
  };

  const variantClasses = {
    primary:
      'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm hover:shadow transition-all',
    secondary:
      'bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 transition-colors',
    outline:
      bookmarked
        ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs hover:bg-blue-100/80 transition-all font-semibold'
        : 'bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 border border-slate-200/90 shadow-2xs transition-all',
    ghost:
      'bg-transparent hover:bg-slate-100 active:bg-slate-200 text-slate-600 transition-colors',
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        title={bookmarked ? 'Bỏ lưu tiện ích' : 'Lưu tiện ích vào danh sách cá nhân'}
        className={`inline-flex items-center justify-center select-none transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 active:scale-95 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      >
        <Bookmark
          className={`w-4 h-4 transition-all duration-200 ${
            bookmarked ? 'fill-blue-600 text-blue-600 scale-110' : 'text-slate-500 hover:text-slate-900'
          }`}
        />
        {showLabel && (
          <span>{bookmarked ? 'Đã Lưu' : 'Lưu Tiện Ích'}</span>
        )}
      </button>

      {/* Floating Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-3 bg-slate-950 text-white text-xs sm:text-sm font-medium rounded-xl shadow-2xl border border-slate-800 animate-in slide-in-from-bottom-3 duration-200">
          <div className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
            <Check className="w-3.5 h-3.5" />
          </div>
          <span>{toastMessage}</span>
        </div>
      )}
    </>
  );
}
